/**
 * Custom addition for Halo & Wick - infinite scroll for paginated product grids.
 *
 * Impact ships with fixed numbered pagination and no setting to change it. This
 * progressively enhances that pagination: the numbered nav is hidden and the next
 * page is fetched and appended as the shopper approaches the end of the grid.
 *
 * It is deliberately defensive:
 *   - If there is no product grid in the same section, it does nothing.
 *   - If a fetch fails, it restores the original numbered pagination.
 *   - With JavaScript off, the original pagination renders untouched.
 *   - A scroll listener backs up the IntersectionObserver.
 *
 * To remove this feature, restore snippets/pagination.liquid to the Impact
 * original and delete this file.
 */

if (!customElements.get('infinite-scroll-pagination')) {
  customElements.define(
    'infinite-scroll-pagination',
    class InfiniteScrollPagination extends HTMLElement {
      connectedCallback() {
        this.loading = false;
        this.threshold = 800;

        this.sectionRoot = this.closest('[id^="shopify-section-"]');
        if (!this.sectionRoot) return;

        this.sectionId = this.sectionRoot.id.replace('shopify-section-', '');
        this.list = this.sectionRoot.querySelector('product-list.product-list');
        this.nav = this.querySelector('nav.pagination');

        // Only enhance product grids. Pages without a product list keep their
        // normal pagination.
        if (!this.list || !this.nav || !this.nextUrl) return;

        this.setAttribute('data-active', '');

        this.status = document.createElement('p');
        this.status.className = 'infinite-scroll-pagination__status';
        this.status.setAttribute('aria-live', 'polite');
        this.appendChild(this.status);

        // Styled inline rather than via the stylesheet: a zero-area element is
        // never reported as intersecting, so the sentinel must have a size even
        // if the CSS fails to load.
        this.sentinel = document.createElement('div');
        this.sentinel.className = 'infinite-scroll-pagination__sentinel';
        this.sentinel.style.cssText = 'display:block;width:100%;height:1px;';
        this.appendChild(this.sentinel);

        this.maybeLoad = this.maybeLoad.bind(this);

        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              if (entries.some((entry) => entry.isIntersecting)) this.loadNext();
            },
            { rootMargin: this.threshold + 'px 0px' }
          );
          this.observer.observe(this.sentinel);
        }

        // Backstop for the observer, and for tabs that start out backgrounded.
        window.addEventListener('scroll', this.maybeLoad, { passive: true });
        window.addEventListener('resize', this.maybeLoad, { passive: true });
        requestAnimationFrame(this.maybeLoad);
      }

      disconnectedCallback() {
        this.teardown();
      }

      teardown() {
        if (this.observer) this.observer.disconnect();
        if (this.maybeLoad) {
          window.removeEventListener('scroll', this.maybeLoad);
          window.removeEventListener('resize', this.maybeLoad);
        }
      }

      get nextUrl() {
        const link = this.nav && this.nav.querySelector('a[rel="next"]');
        return link ? link.getAttribute('href') : null;
      }

      stop() {
        this.teardown();
        if (this.status) this.status.textContent = '';
      }

      /** Restore the theme's own pagination if anything goes wrong. */
      fallBack() {
        this.removeAttribute('data-active');
        this.stop();
      }

      maybeLoad() {
        if (this.loading || !this.sentinel || !this.nextUrl) return;
        if (this.sentinel.getBoundingClientRect().top <= window.innerHeight + this.threshold) {
          this.loadNext();
        }
      }

      async loadNext() {
        if (this.loading) return;

        const next = this.nextUrl;
        if (!next) {
          this.stop();
          return;
        }

        this.loading = true;
        this.status.textContent = this.getAttribute('data-loading-text') || 'Loading more products';

        try {
          const url = new URL(next, window.location.origin);
          url.searchParams.set('section_id', this.sectionId);

          const response = await fetch(url.toString());
          if (!response.ok) throw new Error('Request failed: ' + response.status);

          const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
          const incomingList = parsed.querySelector('product-list.product-list');

          if (incomingList) {
            const fragment = document.createDocumentFragment();
            Array.from(incomingList.children).forEach((node) => {
              // Impact fades cards in via reveal-items, which only tracks the
              // items present when it initialised. Mark appended cards so the
              // stylesheet keeps them visible.
              node.classList.add('is-infinite-loaded');
              fragment.appendChild(node);
            });
            this.list.appendChild(fragment);
          }

          const incomingNav = parsed.querySelector('nav.pagination');
          if (incomingNav) {
            this.nav.innerHTML = incomingNav.innerHTML;
          } else {
            this.nav.remove();
            this.nav = null;
          }

          this.status.textContent = '';
          this.loading = false;

          if (!this.nextUrl) {
            this.stop();
            return;
          }

          // The sentinel may still be in range if the new page was short.
          this.maybeLoad();
        } catch (error) {
          console.error('[infinite-scroll-pagination]', error);
          this.loading = false;
          this.fallBack();
        }
      }
    }
  );
}
